import { useMemo } from "react";
import classNames from "classnames";
import { pluralize } from "@common/helpers/string";
import {
    isTermRecognitionMethod,
    RecognitionMethod,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { Picker } from "@design-system/Picker/Picker.lib";
import { CloseIcon } from "@icons";
import { ScheduleComponentForm } from "./scheduleFormHelpers";
import {
    ALLOCATION_METHOD_CONNECTOR_LABEL,
    buildRecognitionMethodItems,
    buildTermIntervalItems,
    DATE_ANCHOR_OPTIONS,
    PERIOD_COUNT_CONNECTOR_LABEL,
    resolveMilestoneMethodItem,
    toDateAnchorOption,
    toRecognitionAnchor,
} from "./scheduleRecognitionUi";
import { SchedulePercentInput } from "./SchedulePercentInput";
import styles from "./ScheduleDrawer.module.scss";

interface ScheduleComponentRowProps {
    component: ScheduleComponentForm;
    hasTermMethod: boolean;
    isMilestoneAvailable: boolean;
    canRemove: boolean;
    isOnlyComponent: boolean;
    isAllocationLocked: boolean;
    allocationError?: string;
    termLength: number;
    termUnit: ScheduleTermUnit;
    onComponentChange: (patch: Partial<ScheduleComponentForm>) => void;
    onRemove: () => void;
    onTermLengthChange: (termLength: number) => void;
    onTermUnitChange: (termUnit: ScheduleTermUnit) => void;
}

export const ScheduleComponentRow: React.FC<ScheduleComponentRowProps> = ({
    component,
    hasTermMethod,
    isMilestoneAvailable,
    canRemove,
    isOnlyComponent,
    isAllocationLocked,
    allocationError,
    termLength,
    termUnit,
    onComponentChange,
    onRemove,
    onTermLengthChange,
    onTermUnitChange,
}) => {
    const isTermMethod = isTermRecognitionMethod(component.method);
    const isMilestone = component.method === RecognitionMethod.milestone;
    const isTermMethodTaken = hasTermMethod && !isTermMethod;
    const dateAnchorOption = toDateAnchorOption(component.anchor);
    const selectedDateAnchor = DATE_ANCHOR_OPTIONS.find(
        (option) => option.value === dateAnchorOption,
    );
    const methodItems = useMemo(
        () =>
            buildRecognitionMethodItems({
                isTermMethodTaken,
                milestoneItem: resolveMilestoneMethodItem({
                    isMilestoneAvailable,
                    isMilestone,
                    isOnlyComponent,
                }),
            }),
        [isMilestone, isMilestoneAvailable, isOnlyComponent, isTermMethodTaken],
    );
    const termUnitItems = useMemo(
        () => buildTermIntervalItems({ method: component.method }),
        [component.method],
    );

    return (
        <div>
            <HStack className={styles.cardRowFlush} gap={8} align="center" wrap>
                {!isMilestone && (
                    <>
                        <SchedulePercentInput
                            value={component.allocationPercent}
                            isLocked={isAllocationLocked}
                            onChange={(allocationPercent) =>
                                onComponentChange({ allocationPercent })
                            }
                            aria-label="Allocation percent"
                        />
                        <span className={styles.helperText}>
                            {ALLOCATION_METHOD_CONNECTOR_LABEL}
                        </span>
                    </>
                )}
                <Picker.Select
                    items={methodItems}
                    value={component.method}
                    onValueChange={(method) => {
                        if (method == null) {
                            return;
                        }
                        if (
                            isTermRecognitionMethod(method) &&
                            isTermMethodTaken
                        ) {
                            return;
                        }
                        onComponentChange({ method });
                    }}
                />
                {!isMilestone && (
                    <>
                        <span className={styles.helperText}>
                            {isTermMethod ? "start" : "on"}
                        </span>
                        <Picker.Select
                            value={dateAnchorOption}
                            onValueChange={(option) => {
                                if (option == null) {
                                    return;
                                }
                                onComponentChange({
                                    anchor: toRecognitionAnchor(
                                        option,
                                        component.anchor,
                                    ),
                                });
                            }}
                        >
                            <Picker.ButtonTrigger
                                value={selectedDateAnchor?.label}
                                tooltip={selectedDateAnchor?.tooltip}
                            />
                            <Picker.Popup>
                                <Picker.List>
                                    {DATE_ANCHOR_OPTIONS.map((option) => (
                                        <Picker.Item
                                            key={option.value}
                                            value={option.value}
                                            tooltip={option.tooltip}
                                        >
                                            {option.label}
                                        </Picker.Item>
                                    ))}
                                </Picker.List>
                            </Picker.Popup>
                        </Picker.Select>
                    </>
                )}
                {isTermMethod && (
                    <>
                        <Picker.Select
                            items={termUnitItems}
                            value={termUnit}
                            onValueChange={(nextTermUnit) => {
                                if (nextTermUnit == null) {
                                    return;
                                }
                                onTermUnitChange(nextTermUnit);
                            }}
                        />
                        <span className={styles.helperText}>
                            {PERIOD_COUNT_CONNECTOR_LABEL}
                        </span>
                        <Input
                            className={classNames(
                                styles.drawerInput,
                                styles.numericInput,
                                styles.compactInput,
                            )}
                            type="number"
                            min={1}
                            aria-label="Period count"
                            size="md"
                            value={termLength}
                            onChange={(event) =>
                                onTermLengthChange(Number(event.target.value))
                            }
                        />
                        <span className={styles.helperText}>
                            {pluralize("period", termLength)}
                        </span>
                    </>
                )}
                {canRemove && (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        iconOnly
                        aria-label="Remove component"
                        onClick={onRemove}
                    >
                        <CloseIcon />
                    </Button>
                )}
            </HStack>
            {allocationError && (
                <p className={styles.fieldError}>{allocationError}</p>
            )}
        </div>
    );
};
